import { ThemeEvents } from "@theme/events";
import { sectionRenderer } from "@theme/section-renderer";
import { BUNDLE_FLOW_UPDATE_EVENT, bundleState } from "@theme/nugget-bundle";

/** @type {(() => void) | null} */
let cleanup = null;
/** @type {AbortController | null} */
let abortController = null;

function initProgressBar() {
  // Safeguard against rapid re-initialization (e.g. frequent cart updates)
  // so previous listeners/observers/timeouts are always torn down.
  if (cleanup) cleanup();

  const progressBar = document.querySelector(".header-progress-bar");
  if (!(progressBar instanceof HTMLElement)) return;

  const header = document.querySelector("#header-component");
  const headerSection = header?.closest(".shopify-section");
  /** @type {HTMLElement | null} */
  const tooltip = progressBar.querySelector('[ref="progressBarTooltip"]');
  const tooltipAutoHideMsRaw = Number.parseInt(
    progressBar.dataset.tooltipTimeoutMs ?? "",
    10,
  );
  const tooltipAutoHideMs =
    Number.isFinite(tooltipAutoHideMsRaw) && tooltipAutoHideMsRaw > 0
      ? tooltipAutoHideMsRaw
      : 5000;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let tooltipTimeout = null;
  let lastScrollY = 0;
  let ticking = false;
  let naturalTop = header instanceof HTMLElement ? header.offsetHeight : 0;
  /** @type {ResizeObserver | null} */
  let resizeObserver = null;

  // Bundle flow state
  const bundleBuilderPath = progressBar.dataset.bundleBuilderPath ?? "/pages/nugget-bundle-builder";
  const bundleStep1Label = progressBar.dataset.bundleStep1Label ?? "Get Started";
  const bundleStep1LabelMobile = progressBar.dataset.bundleStep1LabelMobile ?? "Get Started";
  const bundleStep2Label = progressBar.dataset.bundleStep2Label ?? "Choose a Box";
  const bundleStep2LabelMobile = progressBar.dataset.bundleStep2LabelMobile ?? "Choose a Box";

  if (header instanceof HTMLElement) {
    resizeObserver = new ResizeObserver(() => {
      naturalTop = header.offsetHeight;
    });
    resizeObserver.observe(header);
  }

  /**
   * Update UI based on bundle flow state
   * In bundle flow:
   * - Step 1 (Get Started) = Bundle Builder page - shows as complete with checkmark
   * - Step 2 (Choose Your Box) = Box selection - shows as initiated/active
   * - Step 3 (Select Add-ons) = Regular addons flow
   */
  function updateBundleFlowUI() {
    if (!(progressBar instanceof HTMLElement)) return;

    const isBundleFlow = bundleState.bundleFlow;
    const step1Container = progressBar.querySelector('[data-step-id="choose_box"]');
    const step1Circle = step1Container?.querySelector(".header-progress-bar__circle");
    const step1Line = step1Container?.querySelector(".header-progress-bar__line");
    const step1Label = progressBar.querySelector('[data-step-id="choose_box"].header-progress-bar__label');
    
    const step2Container = progressBar.querySelector('[data-step-id="select_addons"]');
    const step2Circle = step2Container?.querySelector(".header-progress-bar__circle");
    const step2Label = progressBar.querySelector('[data-step-id="select_addons"].header-progress-bar__label');

    if (!step1Container || !step1Circle || !step1Line || !step1Label) return;
    if (!step2Container || !step2Circle || !step2Label) return;
    if (!(step1Container instanceof HTMLElement)) return;

    if (isBundleFlow) {
      // Step 1: Get Started - show as completed with checkmark
      step1Circle.classList.add("header-progress-bar__circle--filled");
      step1Line.classList.add("header-progress-bar__line--completed");
      
      // Replace step number with checkmark icon
      const stepNumber = step1Circle.querySelector('[data-step-number]');
      if (stepNumber) {
        stepNumber.outerHTML = `<svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 4L3.5 6.5L9 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      }
      
      // Update step 1 labels to "Get Started"
      const step1DesktopLabel = step1Label.querySelector(".header-progress-bar__label-desktop");
      const step1MobileLabel = step1Label.querySelector(".header-progress-bar__label-mobile");
      if (step1DesktopLabel) step1DesktopLabel.textContent = bundleStep1Label;
      if (step1MobileLabel) step1MobileLabel.textContent = bundleStep1LabelMobile;
      
      // Update step 1 path to bundle builder
      step1Container.dataset.path = bundleBuilderPath;
      if (step1Label instanceof HTMLElement) {
        step1Label.dataset.path = bundleBuilderPath;
      }
      
      // Step 2: Update to "Choose Your Box"
      const step2DesktopLabel = step2Label.querySelector(".header-progress-bar__label-desktop");
      const step2MobileLabel = step2Label.querySelector(".header-progress-bar__label-mobile");
      if (step2DesktopLabel) step2DesktopLabel.textContent = bundleStep2Label;
      if (step2MobileLabel) step2MobileLabel.textContent = bundleStep2LabelMobile;
    } else {
      // Restore original state based on cart
      const hasBox = progressBar.dataset.hasBox === "true";
      
      if (hasBox) {
        step1Line.classList.add("header-progress-bar__line--completed");
      } else {
        step1Line.classList.remove("header-progress-bar__line--completed");
      }
      
      // Labels will be restored on next section render
    }
  }

  async function handleCartUpdate() {
    if (!headerSection) return;
    const sectionId = headerSection.id?.replace("shopify-section-", "");
    if (sectionId) {
      if (cleanup) cleanup();
      await sectionRenderer.renderSection(sectionId, { cache: false });
      initProgressBar();
    }
  }

  /** @param {boolean} isScrollingUp */
  function updateVisibilityOnScroll(isScrollingUp) {
    if (!(progressBar instanceof HTMLElement)) return;
    if (!isScrollingUp) {
      progressBar.classList.remove("header-progress-bar--hidden");
    } else {
      progressBar.classList.add("header-progress-bar--hidden");
    }
  }

  /** @param {boolean} isPastNaturalPosition @param {boolean} isScrollingUp */
  function updatePositionState(isPastNaturalPosition, isScrollingUp) {
    if (!(progressBar instanceof HTMLElement)) return;
    if (isPastNaturalPosition) {
      progressBar.classList.add("header-progress-bar--fixed");
      progressBar.classList.remove("header-progress-bar--static");
      updateVisibilityOnScroll(isScrollingUp);
    } else {
      progressBar.classList.remove("header-progress-bar--fixed");
      progressBar.classList.remove("header-progress-bar--hidden");
      progressBar.classList.add("header-progress-bar--static");
    }
  }

function updateProgressBar() {
    // If the dialog is open (body is fixed), do not update the progress bar.
    // This prevents it from resetting to "static" or showing unexpectedly
    // due to the scroll position change.
    if (document.body.style.position === "fixed") {
      ticking = false;
      return;
    }

    const scrollY = window.scrollY;
    const isScrollingUp = scrollY < lastScrollY;
    const isPastNaturalPosition = scrollY > naturalTop;

    updatePositionState(isPastNaturalPosition, isScrollingUp);

    lastScrollY = scrollY;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateProgressBar);
      ticking = true;
    }
  }

  function showTooltip() {
    if (!tooltip) return;
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    tooltip.hidden = false;
    tooltipTimeout = setTimeout(() => {
      tooltip.hidden = true;
      tooltipTimeout = null;
    }, tooltipAutoHideMs);
  }

  /** @param {MouseEvent | KeyboardEvent} event */
  function handleStepClick(event) {
    if (!(progressBar instanceof HTMLElement)) return;

    if (event.type === "keydown" && event instanceof KeyboardEvent) {
      const isEnter = event.key === "Enter" || event.keyCode === 13;
      const isSpace =
        event.key === " " || event.key === "Spacebar" || event.keyCode === 32;

      if (!isEnter && !isSpace) return;
      if (isSpace) event.preventDefault();
    }

    const target =
      event.target instanceof Element
        ? event.target.closest("[data-step-click]")
        : null;
    if (!target) return;

    const stepContainer = target.closest("[data-step-id]");
    if (!(stepContainer instanceof HTMLElement)) return;

    const stepId = stepContainer.dataset.stepId;
    const path = stepContainer.dataset.path;
    const hasBox = progressBar.dataset.hasBox === "true";

    if (stepId === "select_addons" && !hasBox) {
      showTooltip();
      return;
    }

    if (path) {
      window.location.href = path;
    }
  }

  lastScrollY = window.scrollY;
  updateProgressBar();
  
  // Defer bundle flow UI update to ensure DOM is fully ready after section morphs
  requestAnimationFrame(() => {
    updateBundleFlowUI();
  });

  abortController = new AbortController();
  const { signal } = abortController;

  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate, {
    signal,
  });
  document.addEventListener("scroll", onScroll, { passive: true, signal });
  document.addEventListener(BUNDLE_FLOW_UPDATE_EVENT, updateBundleFlowUI, { signal });
  
  // Also update bundle flow UI after section morphs
  document.addEventListener("section:morphed", () => {
    // Small delay to ensure DOM is fully updated
    setTimeout(() => {
      updateBundleFlowUI();
    }, 0);
  }, { signal });
  
  progressBar.addEventListener("click", handleStepClick, { signal });
  progressBar.addEventListener("keydown", handleStepClick, { signal });

  cleanup = () => {
    if (resizeObserver) resizeObserver.disconnect();
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    if (abortController) abortController.abort();
    abortController = null;

    document.removeEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
    document.removeEventListener("scroll", onScroll);
    document.removeEventListener(BUNDLE_FLOW_UPDATE_EVENT, updateBundleFlowUI);
    progressBar.removeEventListener("click", handleStepClick);
    progressBar.removeEventListener("keydown", handleStepClick);
  };
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initProgressBar);
} else {
  initProgressBar();
}
